import { useEffect, useState } from 'react';
import { Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';

interface DeleteConfirmModalProps {
  title: string;
  message: string;
  onConfirm: () => void;
  onClose: () => void;
  confirmLabel?: string;
  confirmationPhrase?: string;
  busy?: boolean;
  error?: string | null;
}

export function DeleteConfirmModal({
  title,
  message,
  onConfirm,
  onClose,
  confirmLabel = 'Usuń',
  confirmationPhrase,
  busy = false,
  error,
}: DeleteConfirmModalProps) {
  const [confirmation, setConfirmation] = useState('');
  const confirmed = !confirmationPhrase || confirmation.trim() === confirmationPhrase;

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !busy) onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [busy, onClose]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => { if (!busy) onClose(); }} />
      <div role="dialog" aria-modal="true" aria-labelledby="delete-dialog-title" className="relative bg-surface-raised rounded-xl border border-border w-full max-w-md p-6 shadow-2xl">
        <h3 id="delete-dialog-title" className="text-base font-semibold text-text-primary mb-2">{title}</h3>
        <p className="text-sm leading-relaxed text-text-secondary">{message}</p>
        {confirmationPhrase && (
          <label className="mt-4 block">
            <span className="mb-2 block text-xs text-text-muted">
              Wpisz <strong className="text-text-primary">{confirmationPhrase}</strong>, aby potwierdzić.
            </span>
            <Input
              autoFocus
              value={confirmation}
              disabled={busy}
              onChange={event => setConfirmation(event.target.value)}
              placeholder={confirmationPhrase}
            />
          </label>
        )}
        {error && (
          <p role="alert" className="mt-4 rounded-lg border border-danger/25 bg-danger/10 p-3 text-xs leading-relaxed text-danger">
            {error}
          </p>
        )}
        <div className="mt-5 flex gap-3">
          <Button
            variant="danger"
            size="sm"
            leftIcon={<Trash2 />}
            onClick={onConfirm}
            disabled={!confirmed || busy}
            loading={busy}
          >
            {confirmLabel}
          </Button>
          <Button
            variant="secondary"
            size="sm"
            onClick={onClose}
            disabled={busy}
          >
            Anuluj
          </Button>
        </div>
      </div>
    </div>
  );
}
